import { peers } from "./protos/eb.peers"
import { IEBSwarmManager, ISwarmErrors, SwarmManager, SwarmErrorsType } from "./swarmManager"
import { Observable } from "rxjs/internal/Observable"
import {
  delay, scan, filter, timeout, map, take,
  mergeMap, catchError, takeUntil, toArray,
} from "rxjs/operators"
import { of, Subject, Subscriber, from, interval, defer, TimeoutError, concat, merge, empty, throwError } from "rxjs"
import { Subscription } from "rxjs/internal/Subscription"
import "whatwg-fetch"
import { IStatsInputPlugin } from "./statsCollectors"
import { manager } from "./protos/eb.manager"
import { crc16 } from "crc"
import { loginfo } from "./utils"
const EVENTCLASS = manager.ArgumentsEvent.EventClass
const ACTIONS = manager.ArgumentsEvent.Actions
const modName = "resource-manager"
interface IEBResourceManager extends IStatsInputPlugin {
  getResource(url: string, timeout: number, ri?: RequestInit): Promise<Uint8Array>
  bindSwarmManager(sm: IEBSwarmManager): IEBResourceManager
  setTimeoutV2V(to: number): IEBResourceManager
  setAllowByteRequest(a: boolean): IEBResourceManager
}

interface IDownloadState {
  // Content url.
  url: string
  startTimestamp: Date
  endTimestamp?: Date
  mode?: "v2v" | "cdn"
  peer?: string
  content?: Uint8Array
}

const setEndState = (ds: IDownloadState, mode: "v2v" | "cdn", st?: Date, peer?: string): IDownloadState => {
  ds.endTimestamp = new Date()
  ds.mode = mode
  if (peer) {
    ds.peer = peer
  }
  if (st) {
    ds.startTimestamp = st
  }
  return ds
}
const createResourceManager = (): IEBResourceManager => {
  return new DResourceManager()
}
const assembleUint8ArrayTo16 = (arr: Uint8Array): Uint16Array => {
  if (arr.length % 2 !== 0) {
    throw new Error("cannot assemble a uint8array which hasn't even number of elements")
  }
  const restored: Uint16Array = new Uint16Array(arr.length / 2)
  for (let i = 0; i < restored.length; ++i ) {
    restored[i] = arr[2 * i] + (arr[2 * i + 1] << 8)
  }
  return restored
}
const encodePossesions = (resources: string[]): Uint16Array => {
  return new Uint16Array(resources.map((value) => crc16(value)))
}
const hasInEncodedPossessions = (url: string, resources: Uint16Array): boolean => {
  // console.info(`want ${crc16(url)} in ${resources}`)
  return resources.indexOf(crc16(url)) !== -1
}
const cutContent = (content: Uint8Array, partLen: number): Uint8Array[] => {
  if (content.length < partLen) {
    return [content]
  }
  const result: Uint8Array[] = []
  for (let i: number = 0; i < content.byteLength; i += partLen) {
    const part: ArrayBuffer = content.buffer.slice(i, i + partLen)
    result.push(new Uint8Array(part))
  }
  return result
}
const assembleParts = (parts: Uint8Array[]): Uint8Array => {
  const size: number = parts.reduce((t: number, p: Uint8Array) => t + p.byteLength, 0)
  const result: Uint8Array = new Uint8Array(size)
  let position: number = 0
  parts.forEach((p: Uint8Array) => {
    result.set(p, position)
    position = position + p.byteLength
  })
  return result
}
const cdnDownload = (url: string, ds: IDownloadState, ri?: RequestInit): Observable<IDownloadState> => {
  return from(fetch(url, ri)).pipe(
    mergeMap((res) => from(res.arrayBuffer())),
    map((value) => {
      ds.content = new Uint8Array(value)
      return ds
    }),
  )
}
class DResourceManager implements IEBResourceManager {
  public freeSlots: number = 3
  private storage: Array<[string, Uint8Array]> = new Array<[string, Uint8Array]>()
  private resourceAvailability: Map<string, Set<string>> = new Map<string, Set<string>>()
  private resourcePreAvailability: Map<string, Set<string>> = new Map<string, Set<string>>()
  private resourcePosessionMap: Map<string, Uint16Array> = new Map<string, Uint16Array>()
  private swarmManager: IEBSwarmManager
  private storageSize: number = 20
  private eventSubject: Subject<manager.Message> = new Subject<manager.Message>()
  private timeoutV2V: number = 8000
  private partSize: number = 15000
  private subscriptionSwarmManager: Subscription
  private allowByteRequest: boolean
  private uploadingSuject: Subject<[string, string, Uint8Array[]]>
  private occupiedSlots: number = 0
  private getResourceCounter: number = 0
  private waitPeerTimeout: number[] = [100, 1000]
  // Uploading State Management,
  private cdnDownloader: (url: string, ds: IDownloadState, ri?: RequestInit)
    => Observable<IDownloadState> = cdnDownload

  public removeToken = (url: string): string => {
    if (url.indexOf("?") !== -1) {
      return url.split("?")[0]
    }
    return url
  }

  public setCDNDownloader =
  (obj: (url: string, ds: IDownloadState, ri?: RequestInit)
      => Observable<IDownloadState> ) => {
    this.cdnDownloader = obj
    return this
  }
  public setStorageSize = (set: number): IEBResourceManager => {
    this.storageSize = set
    return this
  }
  public setRandomWaitForPeers = (set: number[]): IEBResourceManager => {
    this.waitPeerTimeout = set
    return this
  }
  public setAllowByteRequest = (set: boolean): IEBResourceManager => {
    this.allowByteRequest = set
    return this
  }
  public setPartSize = (set: number): IEBResourceManager => {
    this.partSize = set
    return this
  }

  public setTimeoutV2V = (set: number): IEBResourceManager => {
    this.timeoutV2V = set
    return this
  }

  public subscribeEvents = (): Subject<manager.Message> => {
    return this.eventSubject
  }
  // BindSwarmManager binds to a swarmManager and start responding to the mof**ker leechers.
  public bindSwarmManager = (swarmManager: IEBSwarmManager): IEBResourceManager => {
    this.swarmManager = swarmManager
    this.subscriptionSwarmManager  = this.swarmManager.subscribe({
      next: this.handleResourceMessage,
    })

    this.swarmManager.subscribeEvents().pipe(
      filter((msg) => msg.Type === 1025 && // filter the RTC OPEN event
       msg.ArgumentsEvent !== null && msg.ArgumentsEvent.EventName === 0 &&
       msg.ArgumentsEvent.Action === 1 && msg.ArgumentsEvent.RemotePeerID !== null),
    ).subscribe({
      next: (evt) => {
        this.swarmManager.next([evt.ArgumentsEvent.RemotePeerID, this.createPossesionMessage()])
      },
    })
    // Start the resource sending loops
    this.uploadingSuject = new Subject<[string, string, Uint8Array[]]>()
    this.buildSendingPipeline(this.uploadingSuject, this.swarmManager).subscribe()
    return this
  }

  // BuildSendingPipeline is seperated from the BindSwarmManager function
  // So that I can test it seperately
  public buildSendingPipeline = (
    sendingSuject: Subject<[string, string, Uint8Array[]]>,
    swarmManager: IEBSwarmManager,
  ): Observable<ISwarmErrors> => {
    return sendingSuject.pipe(
      filter(() => this.freeSlots > 0), // filter if the uploading slot is occupied
      map((value): [string, string, Uint8Array[], number] => {
        this.freeSlots--
        // sending FreeSlotEvent to stats output plugin.
        this.eventSubject.next(new manager.Message({
          Type: 1025,
          ArgumentsEvent: {
            EventName: EVENTCLASS.RESOURCE,
            Action: ACTIONS.SLOTUPDATE,
            ArgumentsSlotUpdate: {
              FreeSlot: this.freeSlots,
              OccupiedSlot: 3 - this.freeSlots,
            },
          },
        }))
        return [value[0], value[1], value[2], value[2].length]
      }),
      mergeMap((value): Observable<ISwarmErrors> => {
        const startTS = new Date().getTime()
        const peerID = value[0]
        const url = value[1]
        const content = value[2]
        const partTotal = value[3]
        return from(content).pipe(
          map((part, partNum) => new peers.Message({
            Type: peers.MessageType.MessageTypeSatisfy,
            ArgumentsSatisfy: {
              Content: part,
              PartNumber: partNum,
              PartTotal: partTotal,
              Resource: url,
            },
          })),
          mergeMap((satsify) =>
            swarmManager.next([peerID, satsify]).pipe(
              map((theValue) => {
                if (satsify.ArgumentsSatisfy.PartNumber === satsify.ArgumentsSatisfy.PartTotal - 1) {
                  this.freeSlots ++
                  this.eventSubject.next(new manager.Message({
                    Type: 1025,
                    ArgumentsEvent: {
                      EventName: EVENTCLASS.RESOURCE,
                      Action: ACTIONS.UPLOADED,
                      RemotePeerID: peerID,
                      ArgumentsResourceUploaded: {
                        URL: satsify.ArgumentsSatisfy.Resource,
                        TimeSpentMs: Date.now() - startTS,
                        SizeBytes: satsify.ArgumentsSatisfy.PartNumber * this.partSize +
                         satsify.ArgumentsSatisfy.Content.byteLength,
                        Ts: Date.now(),
                        Mode: "v2v",
                        Upload: true,
                      },
                    },
                  }))
                }
                return theValue
              }),
            ),
          ),
        )
      }),
    )
  }

  public getResourceInStorage = (url: string): Uint8Array => {
    for (const entry of this.storage) {
      if (entry[0] === url) {
        return entry[1]
      }
    }
    return null
  }

  public addResourceToStorage = (url: string, content: Uint8Array) => {
    if (this.storage.length >= this.storageSize) {
      // Maybe Notify other peers. TODO
      this.storage.shift()
    }
    this.storage.push([url, new Uint8Array(content)])
  }

  public getAvailablePeersFor = (url: string): Observable<string[]> => {
    return merge(of(0), interval(100)).pipe(
      mergeMap((v) => {
        // if (this.swarmManager.getSwarmSize().All === 0) {return throwError("no peer")} // this
        // line terminate the wait for peers.
        return from(this.resourcePosessionMap).pipe(
          filter((value) => {
            if (!this.swarmManager.hasPeer(value[0])) {
              this.purgePeer(value[0])
              return false
            }
            return true
          }),
          filter((value: [string, Uint16Array]) => hasInEncodedPossessions(url, value[1])),
          map((value) => {
            return value[0]
          }),
          toArray(),
          map((value) => {
            const peerIds = Array.from(value)
            this.eventSubject.next(new manager.Message({
              Type: manager.MessageType.MessageTypeEvent,
              Timestamp: Date.now(),
              ArgumentsEvent: {
                EventName: EVENTCLASS.RESOURCE,
                Action: ACTIONS.AVAILABILITY,
                ArgumentsResourceAvailability: {
                  Pids: peerIds,
                  URL: url,
                },
              },
            }))
            return value
          }),
        )},
      ),
    )
  }

  public purgePeer = (peerID: string) => {
    this.resourceAvailability.forEach((value) => {
      value.delete(peerID)
    })
    this.resourcePreAvailability.forEach((value) => {
      value.delete(peerID)
    })
    this.resourcePosessionMap.delete(peerID)
  }

  public getResource = (url: string, timeoutResource: 15000): Promise<Uint8Array> => {
    // this.swarmManager.broadcastMessage(new peers.Message({
    //   Type: peers.MessageType.MessageTypeDownloading,
    //   ArgumentsResource: {
    //     URL: url,
    //   },
    // }))
    this.getResourceCounter ++
    const oURL = this.removeToken(url)
    const downloadStates: IDownloadState = {
      url: oURL,
      startTimestamp: new Date(),
    }
    return this.getAvailablePeersFor(oURL).pipe(
      filter((value) => value.length !== 0), // ignore if there is no available peers
      take(1),
      map((arrayPeers): [string[], IDownloadState] => {
        return [arrayPeers, downloadStates]
      }),
      timeout(Math.round(Math.random() * (this.waitPeerTimeout[1] - this.waitPeerTimeout[0])
        + this.waitPeerTimeout[0])),
      mergeMap((peerlist: [string[], IDownloadState]): Observable<IDownloadState> =>
        this.p2pDownload(oURL, peerlist[0], peerlist[1]).pipe(
          map((value): IDownloadState => setEndState(downloadStates, "v2v")),
        ),
      ), // use the peerlist to request for stuff
      catchError((err): Observable<IDownloadState> => { // no peer available within the timeout, go cdn
        const st = new Date()
        loginfo(modName, "no peer available after " + (st.getTime() - downloadStates.startTimestamp.getTime()))
        return this.cdnDownloader(url, downloadStates).pipe(
          map((content): IDownloadState => setEndState(downloadStates, "cdn", st)),
        )
      }),
      timeout(this.timeoutV2V), // but still check the timeout if p2p download is not done in time
      catchError((value): Observable<IDownloadState> => { // v2v resouce not in time
        const st = new Date()
        const start = downloadStates.content ? downloadStates.content.byteLength : 0
        if (this.allowByteRequest && start !== 0) {
          loginfo(modName, `Byte request from ${start}`)
          return this.cdnDownloader(url, downloadStates, {
            headers: {
              Range: `bytes = ${downloadStates.content.byteLength} -`,
            },
          }).pipe(
            map((ds) => setEndState(downloadStates, "v2v")),
          )
        } else {
          loginfo(modName, "v2v timeout", start)
          return this.cdnDownloader(url, downloadStates).pipe(
            map((content): IDownloadState => setEndState(downloadStates, "cdn", st)),
          )
        }
      }),
    ).pipe(
      map((value: IDownloadState) => {
        this.eventSubject.next(new manager.Message({
          Type: 1025,
          ArgumentsEvent: {
            EventName: manager.ArgumentsEvent.EventClass.RESOURCE,
            Action: manager.ArgumentsEvent.Actions.DOWNLOADED,
            RemotePeerID: value.peer || "",
            ArgumentsResourceDownloaded: {
              Mode: value.mode,
              URL: value.url,
              Ts: Date.now(),
              TimeSpentMs: value.endTimestamp.getTime() - value.startTimestamp.getTime(),
              SizeBytes: value.content.byteLength,
              Upload: false,
            },
          },
        }))
        return value.content
      }),
      ).toPromise().then((content) => {
      // finish the final step when fetching the resources
      this.addResourceToStorage(oURL, content)
      this.swarmManager.broadcastMessage(this.createPossesionMessage()).subscribe(() => {})
      return content
    })
  }
  private handleResourceMessage = (msg: [string, peers.Message]): void => {
    const peerID = msg[0]
    const resourceArgs = msg[1].ArgumentsResource
    if (msg[1].Type !== peers.MessageType.MessageTypePossession &&
      (!resourceArgs || !resourceArgs.URL || resourceArgs.URL === "")) { return }
    if (!peerID || peerID === "") {
      console.error(modName, `peerID is not valid: ${peerID}`)
      return
    }
    switch (msg[1].Type) {
      case peers.MessageType.MessageTypePossession:
        if (msg[1].ArgumentsPossession && msg[1].ArgumentsPossession.URLHashSet) {
          const urlHash: Uint8Array = msg[1].ArgumentsPossession.URLHashSet
          this.resourcePosessionMap.set(msg[0], assembleUint8ArrayTo16(urlHash))
        }
        break
      case peers.MessageType.MessageTypeRequest:
        const content = this.getResourceInStorage(resourceArgs.URL)
        if (content) { this.uploadingSuject.next([peerID, resourceArgs.URL, cutContent(content, this.partSize)]) }
        break
    }
  }
  private createPossesionMessage = (): peers.Message => {
    const msg = new peers.Message({
      Type: peers.MessageType.MessageTypePossession,
      ArgumentsPossession: {
        URLHashSet: new Uint8Array(encodePossesions(this.storage.map((value) => (value[0])).sort()).buffer),
      },
    })
    return msg
  }
  // emit uint8array
  // or emit swarm errors for the request (i.g. the peer no longer exists.)
  private p2pDownload = (url: string, availPeers: string[], ds: IDownloadState): Observable<IDownloadState> => {
    // TODO: Better way to handle swarm Error
    const startTimestamp = new Date()
    return from(availPeers).pipe(
      take(1),
      mergeMap((peerID: string): Observable<[string, ISwarmErrors]> => {
        ds.peer = peerID
        return this.swarmManager.next([peerID, new peers.Message({
          Type: peers.MessageType.MessageTypeRequest,
          ArgumentsResource: {
            URL: url,
          },
        })]).pipe(map((se: ISwarmErrors) => [peerID, se])) // emit the peer id for later usage
      }),
      map((value: [string, ISwarmErrors]) => {
        return value[0]
      }),
      mergeMap((peerID): Observable<IDownloadState> => {
        const takenParts: Set<number> = new Set<number>()
        const completeSubject = new Subject()
        return this.swarmManager.getPeerChannels().pipe(
          filter((pNpm) => pNpm[0] === peerID), // only listens to a single peer
          filter((pNpm) => pNpm[1].Type === peers.MessageType.MessageTypeSatisfy), // only listens to satisfy message
          filter((pNpm) => pNpm[1].ArgumentsSatisfy &&
            pNpm[1].ArgumentsSatisfy.Resource === url), // only listens to related satisfy
          map((pNpm) => {
            takenParts.add(pNpm[1].ArgumentsSatisfy.PartNumber)
            if (takenParts.size === pNpm[1].ArgumentsSatisfy.PartTotal) {
              completeSubject.next()
            }
            return pNpm[1].ArgumentsSatisfy.Content
          }),
          takeUntil(completeSubject.pipe(delay(10))),
          toArray(),
          map((v) => {
            ds.content = assembleParts(v)
            return ds
          }),
          )
        },
      ),
    )
  }
}

export {
  IEBResourceManager,
  DResourceManager,
  IDownloadState,
  createResourceManager,
  setEndState,
  cutContent,
  assembleParts,
  cdnDownload,
  encodePossesions,
  hasInEncodedPossessions,
  assembleUint8ArrayTo16,
}
